import { motion, useMotionTemplate, useMotionValue } from 'framer-motion';
import { ExternalLink, Github, Layers, Sparkles } from 'lucide-react';
import assets from '../../assets/assets.js';

const ProjectCard = ({ project, index }) => {
  let mouseX = useMotionValue(0);
  let mouseY = useMotionValue(0);

  function handleMouseMove({ currentTarget, clientX, clientY }) {
    let { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className={`group relative border border-white/5 bg-[#111928]/40 backdrop-blur-xl overflow-hidden rounded-[2rem] ${project.featured ? 'md:col-span-2 lg:col-span-2' : ''}`}
      onMouseMove={handleMouseMove}
    >
      <motion.div
        className="pointer-events-none absolute -inset-px rounded-[2rem] opacity-0 transition duration-500 group-hover:opacity-100"
        style={{
          background: useMotionTemplate`
            radial-gradient(
              600px circle at ${mouseX}px ${mouseY}px,
              rgba(255, 255, 255, 0.1),
              transparent 80%
            )
          `,
        }}
      />

      <div className="relative h-full flex flex-col lg:flex-row">
        {/* Image Section */}
        <div className={`relative overflow-hidden ${project.featured ? 'lg:w-[55%] h-72 lg:h-auto' : 'h-56'} bg-[#0b0f19]`}>
          <img
            src={project.image}
            alt={project.title}
            className="h-full w-full object-cover transition-all duration-1000 group-hover:scale-105 opacity-60 group-hover:opacity-90 grayscale group-hover:grayscale-0"
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=2070&auto=format&fit=crop';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111928] via-transparent to-transparent lg:bg-gradient-to-r" />

          <div className="absolute top-6 left-6">
            <span className="px-4 py-1.5 bg-white/10 backdrop-blur-2xl border border-white/20 rounded-full text-[10px] font-black tracking-[0.2em] uppercase text-white flex items-center gap-2 shadow-lg">
              <Sparkles size={12} className="animate-pulse" /> {project.category}
            </span>
          </div>
        </div>

        {/* Content Section */}
        <div className={`p-8 lg:p-10 flex flex-col justify-between ${project.featured ? 'lg:w-[45%]' : 'w-full'}`}>
          <div>
            <h3 className="text-3xl font-black text-white mb-4 group-hover:text-white transition-colors tracking-tighter">
              {project.title}
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed mb-8 font-medium opacity-80 group-hover:opacity-100 transition-opacity line-clamp-4 lg:line-clamp-none">
              {project.description}
            </p>

            {/* Tech Stack Pills */}
            <div className="flex flex-wrap gap-2 mb-8">
              {project.tech.slice(0, project.featured ? 8 : 4).map((tech) => (
                <span key={tech} className="px-3 py-1 bg-white/5 border border-white/10 rounded-lg text-[10px] font-bold uppercase tracking-widest text-white group-hover:border-white/30 transition-all shadow-sm">
                  {tech}
                </span>
              ))}
              {project.tech.length > (project.featured ? 8 : 4) && (
                <span className="px-3 py-1 text-xs text-gray-500 font-bold">+{project.tech.length - (project.featured ? 8 : 4)}</span>
              )}
            </div>
          </div>

          {/* Links */}
          <div className="flex items-center gap-6 mt-auto">
            <a href={project.liveLink} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 text-white hover:text-white transition-all font-black text-xs uppercase tracking-[0.15em] group/link">
              <ExternalLink size={16} className="group-hover/link:-translate-y-0.5 group-hover/link:translate-x-0.5 transition-transform" /> Project
            </a>
            <a href={project.videoLink} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-all font-black text-xs uppercase tracking-[0.15em]">
              <Github size={16} /> Source
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Projects = () => {
  const projectList = [
    {
      title: 'CodeMesher',
      description: `An AI-powered developer intelligence platform designed to analyze, visualize, and improve entire codebases. Features deep repo mapping, context-aware AI chat, and automatic structural visualization.`,
      image: assets.codemesher,
      videoLink: "https://code-mesher.vercel.app/",
      liveLink: "https://code-mesher.vercel.app/",
      tech: ['Electron', 'React', 'TypeScript', 'Node.js', 'LangChain', 'Gen-AI', 'Monaco Editor', 'Three.js'],
      category: 'AI/ML + DevTools',
      featured: true
    },
    {
      title: 'Digi-Kissan',
      description: 'An AgriTech ecosystem leveraging AI for intelligent government scheme recommendations and real-time negotiation insights for farmers.',
      image: assets.kisan,
      videoLink: "https://github.com/Pratik9113/government_scheme.git",
      liveLink: "https://government-scheme.vercel.app/",
      tech: ['MongoDB', 'Node.js', 'Gen-AI', 'Razorpay API'],
      category: 'AI Marketplace',
      featured: false
    },
    {
      title: 'News RAG Chatbot',
      description: 'Advanced chatbot architecture utilizing Retrieval-Augmented Generation to provide high-precision answers across dynamic news corpora.',
      image: assets.news,
      videoLink: "https://github.com/Pratik9113/RAG-Powered-Chatbot-for-News-Websites.git",
      liveLink: "https://rag-powered-chatbot-for-news-websit.vercel.app/",
      tech: ['Redis', 'Express', 'Node.js', 'React'],
      category: 'AI/ML',
      featured: false
    },
    {
      title: 'Truck Map Navigation',
      description: 'Real-time logistics optimization platform built with Flutter, integrating TomTom Maps for intelligent traffic-aware routing.',
      image: assets.truck,
      videoLink: "https://github.com/Pratik9113/Dynamic_map_flutter.git",
      liveLink: "https://github.com/Pratik9113/Dynamic_map_flutter.git",
      tech: ['Flutter', 'TomTom API', 'Firebase', 'AI'],
      category: 'Mobile App',
      featured: false
    },
    {
      title: 'Bail Bridges',
      description: 'Legal automation system using computer vision for face authentication and predictive modeling for bail eligibility assessment.',
      image: assets.bail,
      videoLink: "https://github.com/Pratik9113/SIH2024.git",
      liveLink: "https://github.com/Pratik9113/SIH2024.git",
      tech: ['Node.js', 'MongoDB', 'ML', 'Face Recognition'],
      category: 'Legal Tech',
      featured: false
    },
    {
      title: 'Speechify',
      description: 'Cloud-native AI suite delivering high-precision speech-to-text, multi-lingual translation, and synthetic voice synthesis.',
      image: assets.speechify,
      videoLink: "https://github.com/Pratik9113/Future-ready-talent-Project-speechify.git",
      liveLink: "https://ashy-pond-07114da10.5.azurestaticapps.net/",
      tech: ['Azure Speech', 'JavaScript', 'HTML/CSS'],
      category: 'Cloud AI',
      featured: false
    },
    {
      title: 'Task Management System',
      description: 'Enterprise-grade workflow automation platform featuring real-time task tracking, notification services, and data persistence.',
      image: assets.taskManagement,
      videoLink: "https://github.com/Pratik9113/Task-Management-Web-Application-with-Email-Reminders.git",
      liveLink: "https://task-management-web-application-with-email-reminders-lm64.vercel.app/",
      tech: ['React', 'Node.js', 'MongoDB', 'SendGrid'],
      category: 'Productivity',
      featured: false
    },
  ];

  return (
    <div className="min-h-screen bg-[#0b0f19] py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background Orbs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white/5 rounded-full blur-[150px] -translate-y-1/2 translate-x-1/3" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-black/5 rounded-full blur-[150px] translate-y-1/2 -translate-x-1/3" />

      <div className="max-w-[1400px] mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="mb-24"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-6">
            <Layers className="h-4 w-4 text-white" />
            <span className="text-white text-[10px] font-black tracking-widest uppercase">Portfolio</span>
          </div>
          <h2 className="text-5xl md:text-7xl font-black mb-6 tracking-tighter">
            <span className="text-white">Selected </span>
            <span className="gradient-text">
              Works
            </span>
          </h2>
          <p className="text-gray-300 text-lg md:text-xl max-w-2xl font-medium leading-relaxed">
            Architecting the future through <span className="text-white font-bold">AI Intelligence</span>, scalable architectures, and immersive engineering.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {projectList.map((project, index) => (
            <ProjectCard key={index} project={project} index={index} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Projects;